import append from "../define/append";
import setAttributes from "../define/setAttributes";
import setEvents from "../define/setEvents";
import { resolveFilesPublicPath } from "../../tools/filesPublicPath";
import app404Image from "@Images/404.webp";
import appFaviconImage from "@Images/favicon.ico";
import appLogoImage from "@Images/logo.png";
import appUserImage from "@EPImages/user.png";

const productImages = {
  '404.webp': app404Image,
  'favicon.ico': appFaviconImage,
  'logo.png': appLogoImage,
  'user.png': appUserImage,
};

const normalizeStaticAssetPath = (prefix = '', src = '') => {
  const clean = String(src || '').replace(/^\/+/, '');
  return `${prefix}/${clean}`.replace(/\/{2,}/g, '/');
};

// Qualquer imagem do produto (`@Images/<arquivo>`) resolve pelo contexto do bundler, sem precisar ser listada acima.
let productImageContext = null;
try {
  productImageContext = import.meta.webpackContext('@Images', { recursive: false, regExp: /\.(png|jpe?g|webp|svg|gif|ico)$/ });
} catch { productImageContext = null; }
const fromProductContext = (name) => {
  try {
    const module = productImageContext?.(`./${name}`);
    return module ? (module.default ?? module) : null;
  } catch { return null; }
};

export const resolveProductImageSource = (src = '') => {
  const clean = String(src || '').replace(/^\//, '');
  if (!clean) return clean;
  return productImages[clean] || fromProductContext(clean) || clean;
};

export const resolveKnownImageSource = (src = '', defaultOrigin = 'product') => {
  const raw = String(src || '').trim();
  if (!raw) return raw;

  if (raw.startsWith('@Images/')) {
    return resolveProductImageSource(raw.slice('@Images/'.length));
  }

  if (raw.startsWith('@EPImages/')) {
    return normalizeStaticAssetPath('/files/images', raw.slice('@EPImages/'.length));
  }

  if (raw.startsWith('@EPComponentImages/')) {
    return normalizeStaticAssetPath('/files/component-images', raw.slice('@EPComponentImages/'.length));
  }

  if (/^(https?:\/\/|\/)/i.test(raw)) {
    return raw;
  }

  if (defaultOrigin === 'product') {
    return resolveProductImageSource(raw);
  }

  return raw;
};

class Images {
  #toAssetPath(src = '') {
    return String(src || '').replace(/^\//, '');
  }

  async #resolveProductImage(src = '') {
    return resolveProductImageSource(this.#toAssetPath(src));
  }

  async #resolveExternalSource(src = '') {
    const raw = String(src || '').trim();
    if (!raw.startsWith('@')) return raw;

    const aliasResolvers = [
      { prefix: '@Images/', resolve: (value) => this.#resolveProductImage(value) },
      { prefix: '@EPImages/', resolve: (value) => this.#resolveEPImage(value) },
      { prefix: '@EPComponentImages/', resolve: (value) => this.#resolveComponentImage(value) },
    ];

    for (const { prefix, resolve } of aliasResolvers) {
      if (raw.startsWith(prefix)) {
        return await resolve(raw.slice(prefix.length));
      }
    }

    return raw;
  }

  #resolveEPImage(src = '') {
    return normalizeStaticAssetPath('/files/images', src);
  }

  #resolveComponentImage(src = '') {
    return normalizeStaticAssetPath('/files/component-images', src);
  }

  async image(options) {
    let figure;

    if (options.hasFigure !== false) {
      figure = document.createElement('figure');
      const figureOptions = {
        element: 'figure',
        parent: options.parent,
        id: (options.id !== undefined) ? (`${options.id}-figure`) : (undefined),
        row: options.row,
        col: options.col,
        class: options.fbClass,
        style: options.fbStyle,
        tooltip: options.tooltip
      };

      append(figure, figureOptions);

      figure = setAttributes(figure, figureOptions);
      figure = setEvents(figure, figureOptions);

      delete options.tooltip;
      delete options.row;
      delete options.col;
      options.parent = figure;

      if (options.label !== undefined && typeof options.label === 'string') {
        let figcaption = document.createElement('figcaption');
        const fc = {
          parent: options.parent,
          id: (options.id !== undefined) ? (`${options.id}-figcaption`) : (undefined),
          class: (options.labelPosition !== undefined && options.labelPosition === 'top') ? ('top-caption') : ('bottom-caption'),
          text: options.label
        };

        append(figcaption, fc);

        figcaption = setAttributes(figcaption, fc);
        figcaption = setEvents(figcaption, fc);

        if (options.labelPosition !== undefined && options.labelPosition === 'top') {
          options.append = 'after';
          options.class = 'bottom-image';
        } else {
          options.append = 'before';
          options.class = 'top-image';
        }

        options.parent = figcaption;
      } else {
        options.class = 'center-image';
      }
    }

    /* Cria a tag de imagem */
    let image = document.createElement('img');
    append(image, options);
    const check = ['product', 'media', 'ep', 'component', 'external'];
    options.origin = options.origin || 'media';

    if (check.includes(options.origin)) {
      if (options.origin === 'product') {
        options.src = await this.#resolveProductImage(options.src);
      } else if (options.origin === 'ep') {
        options.src = this.#resolveEPImage(options.src);
      } else if (options.origin === 'component') {
        options.src = this.#resolveComponentImage(options.src);
      } else if (options.origin === 'external') {
        options.src = await this.#resolveExternalSource(options.src);
      } else {
        options.src = resolveFilesPublicPath(options.src);
      }
    }

    delete options.origin;
    image = setAttributes(image, options);
    image = setEvents(image, options);

    return null;
  }
}

const images = new Images();
export const image = (options) => images.image(options);
